import { SUPPORTED_SCOPES } from "../shared/constants";
import type { OidcApplication, TokenEndpointAuthMethod } from "../shared/types";
import type { ApplicationDraft } from "./types";

interface SharedProps {
  draft: ApplicationDraft;
  onPatch: (patch: Partial<ApplicationDraft>) => void;
}

export function ApplicationGeneralFields({
  application,
  draft,
  onPatch,
}: SharedProps & { application?: OidcApplication }) {
  return (
    <section
      className="configuration-section"
      aria-labelledby="general-heading"
    >
      <div className="configuration-section__heading">
        <span className="configuration-section__index">01</span>
        <div>
          <h3 id="general-heading">General</h3>
          <p>Identity, ownership, and lifecycle.</p>
        </div>
      </div>
      <div className="form-grid">
        <label>
          <span>Display name</span>
          <input
            data-autofocus={application ? undefined : true}
            maxLength={120}
            onChange={(event) => onPatch({ displayName: event.target.value })}
            required
            value={draft.displayName}
          />
        </label>
        <label>
          <span>Client ID</span>
          <input
            maxLength={128}
            onChange={(event) => onPatch({ clientId: event.target.value })}
            pattern="[A-Za-z0-9][A-Za-z0-9._~-]{2,127}"
            readOnly={Boolean(application)}
            required
            value={draft.clientId}
          />
          {application && <small>Immutable after registration.</small>}
        </label>
        <label>
          <span>Owner</span>
          <input
            maxLength={120}
            onChange={(event) => onPatch({ owner: event.target.value })}
            required
            value={draft.owner}
          />
        </label>
        <label>
          <span>Environment</span>
          <select
            onChange={(event) =>
              onPatch({
                environment: event.target
                  .value as ApplicationDraft["environment"],
              })
            }
            value={draft.environment}
          >
            <option value="production">Production</option>
            <option value="staging">Staging</option>
            <option value="development">Development</option>
          </select>
        </label>
        <label>
          <span>Status</span>
          <select
            onChange={(event) =>
              onPatch({
                status: event.target.value as ApplicationDraft["status"],
              })
            }
            value={draft.status}
          >
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
        </label>
        <label className="form-grid__wide">
          <span>
            Description <em>Optional</em>
          </span>
          <textarea
            maxLength={500}
            onChange={(event) => onPatch({ description: event.target.value })}
            placeholder="What uses this client and who owns it?"
            rows={2}
            value={draft.description}
          />
        </label>
      </div>
    </section>
  );
}

export function ApplicationConnectionFields({
  draft,
  onPatch,
  redirectText,
  onRedirectTextChange,
}: SharedProps & {
  redirectText: string;
  onRedirectTextChange: (value: string) => void;
}) {
  return (
    <section
      className="configuration-section"
      aria-labelledby="connection-heading"
    >
      <div className="configuration-section__heading">
        <span className="configuration-section__index">02</span>
        <div>
          <h3 id="connection-heading">Connection & authentication</h3>
          <p>
            Callbacks are exact matches; wildcards and fragments are rejected.
          </p>
        </div>
      </div>
      <div className="form-grid">
        <label className="form-grid__wide">
          <span>Redirect URLs</span>
          <textarea
            className="code-input"
            onChange={(event) => onRedirectTextChange(event.target.value)}
            required
            rows={3}
            spellCheck={false}
            value={redirectText}
          />
          <small>
            One exact URL per line. HTTPS is required outside localhost.
          </small>
        </label>
        <label>
          <span>Token endpoint authentication</span>
          <select
            onChange={(event) =>
              onPatch({
                tokenEndpointAuthMethod: event.target
                  .value as TokenEndpointAuthMethod,
              })
            }
            value={draft.tokenEndpointAuthMethod}
          >
            <option value="client_secret_basic">
              Client secret Basic (recommended)
            </option>
            <option value="client_secret_post">Client secret POST</option>
            <option value="none">Public client (no secret)</option>
          </select>
        </label>
        <label>
          <span>Login experience</span>
          <select
            onChange={(event) =>
              onPatch({
                loginMode: event.target.value as ApplicationDraft["loginMode"],
              })
            }
            value={draft.loginMode}
          >
            <option value="standard">Username and password</option>
            <option value="fixed">Password-only fixed account</option>
          </select>
        </label>
        {draft.loginMode === "fixed" && (
          <label className="form-grid__wide">
            <span>Fixed directory username</span>
            <input
              maxLength={64}
              onChange={(event) =>
                onPatch({ fixedUsername: event.target.value })
              }
              required
              value={draft.fixedUsername ?? ""}
            />
            <small>
              The account must exist and be active. Browser parameters cannot
              override it.
            </small>
          </label>
        )}
      </div>
    </section>
  );
}

export function ApplicationPolicyFields({ draft, onPatch }: SharedProps) {
  function toggleScope(scope: string) {
    if (scope === "openid") return;
    onPatch({
      allowedScopes: draft.allowedScopes.includes(scope)
        ? draft.allowedScopes.filter((value) => value !== scope)
        : [...draft.allowedScopes, scope],
    });
  }

  return (
    <section className="configuration-section" aria-labelledby="policy-heading">
      <div className="configuration-section__heading">
        <span className="configuration-section__index">03</span>
        <div>
          <h3 id="policy-heading">Scopes & token lifetimes</h3>
          <p>Expose only required claims and keep credentials short-lived.</p>
        </div>
      </div>
      <div className="configuration-section__content">
        <fieldset className="scope-fieldset">
          <legend>Allowed scopes</legend>
          <div className="scope-picker">
            {SUPPORTED_SCOPES.map((scope) => (
              <label key={scope}>
                <input
                  checked={draft.allowedScopes.includes(scope)}
                  disabled={scope === "openid"}
                  onChange={() => toggleScope(scope)}
                  type="checkbox"
                />
                <span>{scope}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="form-grid ttl-grid">
          <label>
            <span>Access token</span>
            <div className="unit-field">
              <input
                max={3600}
                min={60}
                onChange={(event) =>
                  onPatch({ accessTokenTtlSeconds: Number(event.target.value) })
                }
                required
                type="number"
                value={draft.accessTokenTtlSeconds}
              />
              <span>seconds</span>
            </div>
            <small>Allowed range: 60–3600</small>
          </label>
          <label>
            <span>Authorization code</span>
            <div className="unit-field">
              <input
                max={300}
                min={30}
                onChange={(event) =>
                  onPatch({
                    authorizationCodeTtlSeconds: Number(event.target.value),
                  })
                }
                required
                type="number"
                value={draft.authorizationCodeTtlSeconds}
              />
              <span>seconds</span>
            </div>
            <small>Allowed range: 30–300</small>
          </label>
          <label>
            <span>Login transaction</span>
            <div className="unit-field">
              <input
                max={1800}
                min={120}
                onChange={(event) =>
                  onPatch({
                    authTransactionTtlSeconds: Number(event.target.value),
                  })
                }
                required
                type="number"
                value={draft.authTransactionTtlSeconds}
              />
              <span>seconds</span>
            </div>
            <small>Allowed range: 120–1800</small>
          </label>
        </div>
      </div>
    </section>
  );
}
